import { useEffect, useRef } from 'react'
import { DoorOpen, Mic, Trash2, Unlink } from 'lucide-react'

import { blurAfterMouseClick } from './blurAfterMouseClick'
import { InlineTextField } from './InlineTextField'
import { Kbd } from './Kbd'

import styles from './RosterBar.module.scss'

import type { GtRoom, GtSpeaker } from '@/api/internal'
import { toSpeakerIdentity } from '@/pages/app/AttributionViewer/annotate/gtDerivations'
import {
  canGroupIntoRoom,
  labelOfSpeaker,
  roomDepartureHasNameCollision,
  SPEAKER_NAME_MAX_CODEPOINTS,
} from '@/pages/app/AttributionViewer/annotate/gtModel'
import { type SpeakerStyleMap, styleForSpeaker } from '@/pages/app/AttributionViewer/components/colors'

export interface RosterBarProps {
  speakers: readonly GtSpeaker[]
  rooms: readonly GtRoom[]
  turnCounts?: Map<string, number>
  speakerStyles: SpeakerStyleMap
  adding: boolean
  onStartAdd: () => void
  onAdd: (name: string) => void
  onCancelAdd: () => void
  onRenameRequest: (speakerId: string) => void
  onDeleteRequest: (speakerId: string) => void
  onToggleRecorder: (speakerId: string) => void
  /** Roster speakers ticked for the next "Group as room" or "Delete". */
  selectedIds: ReadonlySet<string>
  onToggleSelect: (speakerId: string) => void
  onGroupRoom: () => void
  onDeleteSelected: () => void
  onRenameRoom: (roomId: string) => void
  onUngroupRoom: (roomId: string) => void
  onLeaveRoom: (speakerId: string) => void
  disabled: boolean
}

interface SpeakerChipProps {
  speaker: GtSpeaker
  /** 1-based position among its room's voices; only for room voices. */
  voiceOrdinal?: number
  count: number
  color: string
  selected: boolean
  disabled: boolean
  onRenameRequest: (speakerId: string) => void
  onDeleteRequest: (speakerId: string) => void
  onToggleRecorder: (speakerId: string) => void
  onToggleSelect: (speakerId: string) => void
  onLeaveRoom: (speakerId: string) => void
}

const DOUBLE_CLICK_MS = 250

const SpeakerChip = ({
  speaker,
  voiceOrdinal,
  count,
  color,
  selected,
  disabled,
  onRenameRequest,
  onDeleteRequest,
  onToggleRecorder,
  onToggleSelect,
  onLeaveRoom,
}: SpeakerChipProps) => {
  const isRoomVoice = speaker.kind === 'room'
  // A single click renames on a delay so a double-click can cancel it and delete instead.
  const renameTimer = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (renameTimer.current != null) window.clearTimeout(renameTimer.current)
    },
    []
  )
  const onNameClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.detail === 0) {
      onRenameRequest(speaker.id)
      return
    }
    if (event.detail >= 2) {
      if (renameTimer.current != null) {
        window.clearTimeout(renameTimer.current)
        renameTimer.current = null
      }
      onDeleteRequest(speaker.id)
      return
    }
    if (renameTimer.current != null) window.clearTimeout(renameTimer.current)
    renameTimer.current = window.setTimeout(() => {
      renameTimer.current = null
      onRenameRequest(speaker.id)
    }, DOUBLE_CLICK_MS)
  }
  return (
    <span
      className={isRoomVoice ? styles.voiceChip : styles.chip}
      data-kind={speaker.kind}
      data-speaker-selection-target
    >
      {labelOfSpeaker(speaker) === null && (
        <input
          type="checkbox"
          className={styles.select}
          aria-label={`Select ${speaker.voice_name ?? speaker.name}`}
          checked={selected}
          disabled={disabled}
          onChange={() => onToggleSelect(speaker.id)}
        />
      )}
      <span className={styles.dot} style={{ backgroundColor: color }} />
      {voiceOrdinal !== undefined && <span className={styles.voiceOrdinal}>v{voiceOrdinal}</span>}
      <button
        type="button"
        className={styles.nameButton}
        title={
          isRoomVoice ? `Name voice ${voiceOrdinal} (double-click to delete)` : 'Edit speaker (double-click to delete)'
        }
        aria-label={isRoomVoice ? `Name voice ${voiceOrdinal} (${speaker.name})` : undefined}
        disabled={disabled}
        onClick={onNameClick}
      >
        <span className={styles.name}>{isRoomVoice ? (speaker.voice_name ?? speaker.name) : speaker.name}</span>
        <span className={styles.pen} aria-hidden="true">
          ✎
        </span>
      </button>
      <span className={styles.count}>{count}</span>
      {labelOfSpeaker(speaker) === null && (
        <button
          type="button"
          className={styles.iconButton}
          title={isRoomVoice ? 'Name or remove voice' : 'Rename or join room'}
          aria-label={`${isRoomVoice ? 'Edit room voice' : 'Edit speaker'} ${speaker.voice_name ?? speaker.name}`}
          disabled={disabled}
          onClick={() => onRenameRequest(speaker.id)}
        >
          <DoorOpen size={11} aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className={styles.recorderToggle}
        aria-pressed={speaker.is_recorder}
        title={speaker.is_recorder ? 'Recorder' : 'Mark as recorder'}
        disabled={disabled}
        onClick={() => onToggleRecorder(speaker.id)}
      >
        <Mic size={12} aria-hidden="true" />
      </button>
      {speaker.hotkey && <Kbd>{speaker.hotkey}</Kbd>}
      {isRoomVoice && (
        <button
          type="button"
          className={styles.iconButton}
          title="Remove from room (becomes a person again)"
          aria-label={`Remove ${speaker.name} from room`}
          disabled={disabled}
          onClick={() => onLeaveRoom(speaker.id)}
        >
          <Unlink size={11} aria-hidden="true" />
        </button>
      )}
    </span>
  )
}

export const RosterBar = ({
  speakers,
  rooms,
  turnCounts,
  speakerStyles,
  adding,
  onStartAdd,
  onAdd,
  onCancelAdd,
  onRenameRequest,
  onDeleteRequest,
  onToggleRecorder,
  selectedIds,
  onToggleSelect,
  onGroupRoom,
  onDeleteSelected,
  onRenameRoom,
  onUngroupRoom,
  onLeaveRoom,
  disabled,
}: RosterBarProps) => {
  const chipFor = (speaker: GtSpeaker, voiceOrdinal?: number) => (
    <SpeakerChip
      key={speaker.id}
      speaker={speaker}
      voiceOrdinal={voiceOrdinal}
      count={turnCounts?.get(speaker.id) ?? 0}
      color={styleForSpeaker(toSpeakerIdentity(speaker), speakerStyles).color}
      selected={selectedIds.has(speaker.id)}
      disabled={disabled}
      onRenameRequest={onRenameRequest}
      onDeleteRequest={onDeleteRequest}
      onToggleRecorder={onToggleRecorder}
      onToggleSelect={onToggleSelect}
      onLeaveRoom={onLeaveRoom}
    />
  )
  const selectedCount = speakers.filter((speaker) => selectedIds.has(speaker.id)).length
  const canGroup = canGroupIntoRoom(selectedCount, rooms.length)

  return (
    <div className={styles.bar} aria-label="Speaker roster" onClick={blurAfterMouseClick}>
      {speakers.filter((speaker) => speaker.room_id === null).map((speaker) => chipFor(speaker))}
      {rooms.map((room) => {
        const voices = speakers.filter((speaker) => speaker.room_id === room.id)
        const breakUpCollision = roomDepartureHasNameCollision(speakers, new Set(voices.map((voice) => voice.id)))
        return (
          <span key={room.id} className={styles.room} role="group" aria-label={`Room ${room.label}`}>
            <span className={styles.roomHeader}>
              <DoorOpen size={12} aria-hidden="true" />
              <button
                type="button"
                className={styles.roomLabelButton}
                title="Rename room"
                disabled={disabled}
                onClick={() => onRenameRoom(room.id)}
              >
                <span className={styles.roomLabel}>{room.label}</span>
                <span className={styles.pen} aria-hidden="true">
                  ✎
                </span>
              </button>
              <span className={styles.roomMeta}>
                {voices.length} {voices.length === 1 ? 'voice' : 'voices'}
              </span>
              <button
                type="button"
                className={styles.breakUpButton}
                title={
                  breakUpCollision
                    ? 'Rename or clear conflicting voice names before breaking up the room'
                    : 'Break the room up into individual person speakers (undoable)'
                }
                disabled={disabled || breakUpCollision}
                onClick={() => onUngroupRoom(room.id)}
              >
                Break up
              </button>
            </span>
            {voices.map((speaker, index) => chipFor(speaker, index + 1))}
          </span>
        )
      })}
      {adding ? (
        <span className={styles.chipEditor}>
          <InlineTextField
            initialValue=""
            placeholder="new speaker name"
            ariaLabel="New speaker name"
            maxLength={SPEAKER_NAME_MAX_CODEPOINTS}
            onCommit={(value) => {
              const next = value.trim()
              if (next) onAdd(next)
              else onCancelAdd()
            }}
            onCancel={onCancelAdd}
          />
        </span>
      ) : (
        <button type="button" className={styles.addButton} disabled={disabled} onClick={onStartAdd}>
          + Add speaker
          <Kbd>A</Kbd>
        </button>
      )}
      <button
        type="button"
        className={styles.groupButton}
        title={
          canGroup && selectedCount === 1
            ? 'Add the ticked speaker to an existing room'
            : 'Mark the ticked speakers as voices of one room'
        }
        data-speaker-selection-target
        disabled={disabled || !canGroup}
        onClick={onGroupRoom}
      >
        <DoorOpen size={12} aria-hidden="true" />
        Group as room{selectedCount > 0 ? ` (${selectedCount})` : ''}
        {/* With one speaker ticked, G edits that speaker instead of opening this dialog. */}
        {selectedCount !== 1 && <Kbd>G</Kbd>}
      </button>
      <button
        type="button"
        className={styles.deleteButton}
        title="Delete the ticked speakers (undoable)"
        disabled={disabled || selectedCount === 0}
        onClick={onDeleteSelected}
      >
        <Trash2 size={12} aria-hidden="true" />
        Delete{selectedCount > 0 ? ` (${selectedCount})` : ''}
        <Kbd>⌫</Kbd>
      </button>
    </div>
  )
}
